import { toDate } from '@/utils/flowSightPeriods'
import { HOUR_LABELS, hourIndexFromHour } from '@/utils/flowSightHourBuckets'

// One crossfilter reduce per counterparty feeding all three visuals of a
// Portfolio row: the daily sparkline, the maturity bubbles and the average
// maturity tick. Splitting them into three groups would walk the same rows
// three times and let them disagree after a filter.
//
// Volume is NEGOTIATED (DEAL + NOTH.DONE, in BRL) - the tab's chosen basis.
// Rows outside that universe never reach here; Portfolio.vue filters them out
// the same way the Conversions tab does.

// Eixo em HORA DO DIA, nao em prazo: no relatorio de spot todo prazo e de 0 a 2
// dias e a raia ficaria com todas as bolhas empilhadas num ponto. Reexportado
// daqui para o painel nao precisar conhecer a escada.
export { HOUR_AXIS_MIN, HOUR_AXIS_MAX } from '@/utils/flowSightHourBuckets'

const dayKey = (value) => {
  const date = toDate(value)
  if (!date) return null
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export const emptyClientValue = () => ({
  cnt: 0,
  vol: 0,
  // day -> BRL. A Map, not an array: the reduce has to survive removals, and
  // crossfilter calls remove once per row leaving the filter.
  byDay: new Map(),
  // indice da faixa HORARIA -> BRL. Alimenta as barras empilhadas do ranking:
  // o MIX de horario e o que separa um cliente que so opera na abertura de
  // outro espalhado pela tarde.
  byBand: new Map(),
  // Media simples da hora do RFQ - NAO ponderada por volume. Guardada como
  // soma/contagem para add e remove continuarem exatos.
  hourSum: 0,
  hourCount: 0,
})

const bump = (map, key, delta) => {
  if (key === null || key === undefined) return
  const next = (map.get(key) || 0) + delta
  // Drop the key at zero so an emptied day/bin disappears instead of lingering
  // as a 0 that still draws a point.
  if (Math.abs(next) < 1e-6) map.delete(key)
  else map.set(key, next)
}

// amountOf(row) must return BRL - pass store.convertNotionalToBRL.
export function createClientPortfolioReducer (amountOf) {
  const apply = (p, v, sign) => {
    const amount = amountOf(v) * sign
    const hora = Number(v?.hourOfDay)
    const temHora = Number.isFinite(hora)

    p.cnt += sign
    p.vol += amount
    bump(p.byDay, dayKey(v?.rfqtimestamp), amount)
    if (temHora) {
      bump(p.byBand, hourIndexFromHour(hora), amount)
      p.hourSum += hora * sign
      p.hourCount += sign
    }
    return p
  }

  return {
    add: (p, v) => apply(p, v, 1),
    rem: (p, v) => apply(p, v, -1),
    init: emptyClientValue,
  }
}

// `cnt` is the exact emptiness test - `vol` is a float sum kept incrementally
// and does not return to exactly 0 when a filter empties the group.
export const hasRows = (value) => !!value && value.cnt > 0

export const totalVolumeOf = (value) => (hasRows(value) ? value.vol : 0)

export const averageHourOf = (value) =>
  (hasRows(value) && value.hourCount > 0 ? value.hourSum / value.hourCount : null)

// [{ day, value }] sorted by day, over the union of days passed in - so every
// row's sparkline shares one x scale and a client with no trade on a given day
// shows a gap at the same place as everyone else.
export function dailySeries (value, days) {
  if (!hasRows(value)) return []
  return days.map((day) => ({ day, value: value.byDay.get(day) || 0 }))
}

// Volume divided by number of RFQs - the per-unit metric that reorders the
// ranking: the biggest client by total can be mid-table by ticket, and vice
// versa. Same idea as the reference chart's "per resident adult".
export const averageTicketOf = (value) =>
  (hasRows(value) ? value.vol / value.cnt : 0)

// [{ band, label, value }] over ALL bands, in order, so a stacked bar always
// has the same segment order even when a client skips a band.
export function hourBandMix (value) {
  if (!hasRows(value)) return []
  return HOUR_LABELS.map((label, band) => ({
    band,
    label,
    value: value.byBand.get(band) || 0,
  }))
}

// Running total over the shared day axis. Cumulative rather than per-day
// because the exports cover ~20 trading days and the median client trades on
// 5 of them: a daily line is mostly gaps, while a cumulative one is monotone
// and readable with few points.
export function cumulativeSeries (value, days) {
  if (!hasRows(value)) return []
  let total = 0
  return days.map((day) => {
    total += value.byDay.get(day) || 0
    return { day, value: total }
  })
}
